// pages/stock/Stock.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Grid, Typography, Button,
  CircularProgress, Fab, Chip, FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, TablePagination, IconButton, Stack,
  TextField, Tooltip, Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FileDownload as FileDownloadIcon,
  FilterList,
  CalendarMonth as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  SwapHoriz as SwapHorizIcon,
  Inventory as InventoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { stockAPI, branchAPI, stockCategoryAPI } from '../../services/api';
import NoContent from '../../components/common/NoContent';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import NoPrivilege from '../../components/common/NoPrivilege';

// Separated components
import StockForm from './StockForm.jsx';
import StockDailySnapshots from './StockDailySnapshots';
import StockWACHistory from './StockWACHistory';
import BranchTransferDialog from './BranchTransferDialog';
import StockAuditDialog from './StockAuditDialog';

const Stock = () => {
  const { t } = useTranslation();
  const { user, isSuperAdmin } = useAuth();

  // ── Data state ──────────────────────────────────────────────────────
  const [stockItems, setStockItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [categories, setCategories] = useState([]);

  // ── UI state ────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [privilegeError, setPrivilegeError] = useState(false);
  const [exporting, setExporting] = useState(false);

  // ── Dialog states ───────────────────────────────────────────────────
  const [snapshotsDialog, setSnapshotsDialog] = useState({
    open: false, item: null, branchId: null, branchName: null
  });
  const [wacHistoryDialog, setWacHistoryDialog] = useState({
    open: false, item: null, branchId: null, branchName: null
  });
  const [transferDialog, setTransferDialog] = useState({
    open: false, item: null
  });
  const [auditDialog, setAuditDialog] = useState({
    open: false, item: null
  });

  // ── Expanded rows ───────────────────────────────────────────────────
  const [expandedRows, setExpandedRows] = useState(new Set());

  // ── Pagination ────────────────────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalDocs, setTotalDocs] = useState(0);

  // ── Filters ─────────────────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [filters, setFilters] = useState({
    lowStock: false,
    expired: false,
    showInactive: false,
  });

  // ── Debounce search ─────────────────────────────────────────────────
  useEffect(() => {
    const id = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(0);
    }, 500);
    return () => clearTimeout(id);
  }, [searchInput]);

  // ── Fetch stock items ───────────────────────────────────────────────
  const fetchStockItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (!filters.showInactive) params.active = 'true';
      if (searchQuery) params.name = searchQuery;
      if (selectedBranch) params.branch = selectedBranch;
      if (selectedCategory) params.category = selectedCategory;
      if (filters.expired) params.expired = 'true';
      if (filters.lowStock) params.lowStock = 'true';

      let response;
      if (filters.lowStock) {
        response = await stockAPI.getLowStock(params);
      } else if (filters.expired) {
        response = await stockAPI.getExpired(params);
      } else {
        response = await stockAPI.getAll(params);
      }

      const { data, pagination } = response.data;
      setStockItems(Array.isArray(data) ? data : []);
      setTotalDocs(pagination?.total ?? 0);
    } catch (error) {
      if (error.response?.data?.message?.toLowerCase().includes('insufficient privileges')) {
        setPrivilegeError(true);
      } else {
        toast.error(error.response?.data?.message || t('stock.fetchError'));
      }
      setStockItems([]);
      setTotalDocs(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, filters, selectedBranch, selectedCategory, t]);

  useEffect(() => { fetchStockItems(); }, [fetchStockItems]);

  // ── Fetch branches ──────────────────────────────────────────────────
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await branchAPI.getActiveBranches();
        setBranches(res.data.data || []);
      } catch {
        toast.error(t('branches.fetchError'));
      }
    };
    fetchBranches();
  }, [t]);

  // ── Fetch categories ────────────────────────────────────────────────
  useEffect(() => {
    const fetchCategories = async () => {
      setCategoriesLoading(true);
      try {
        const res = await stockCategoryAPI.getAll({ isActive: true, limit: 100 });
        const list = res.data?.data ?? res.data ?? [];
        setCategories(Array.isArray(list) ? list : []);
      } catch {
        toast.error(t('stockCategory.fetchError', 'Failed to load categories'));
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, [t]);

  // ── Lock branch for non-superadmin ──────────────────────────────────
  useEffect(() => {
    if (!isSuperAdmin && user?.branch) {
      setSelectedBranch(user.branch);
    }
  }, [isSuperAdmin, user]);

  // ── Filter helpers ──────────────────────────────────────────────────
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => {
      const next = { ...prev };
      if (filterType === 'lowStock') {
        next.lowStock = value;
        if (value) next.expired = false;
      } else if (filterType === 'expired') {
        next.expired = value;
        if (value) next.lowStock = false;
      } else {
        next[filterType] = value;
      }
      return next;
    });
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({ lowStock: false, expired: false, showInactive: false });
    setSearchInput('');
    setSelectedCategory('');
    setSelectedBranch(isSuperAdmin ? '' : user?.branch || '');
    setPage(0);
  };

  // ── Export ──────────────────────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (selectedBranch) params.branch = selectedBranch;
      if (selectedCategory) params.category = selectedCategory;
      if (filters.lowStock) params.lowStock = true;
      if (filters.expired) params.expired = true;
      if (!filters.showInactive) params.active = 'true';

      const response = await stockAPI.export(params);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `stock_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t('stock.exportSuccess', 'Stock exported successfully'));
    } catch (err) {
      toast.error(err.response?.data?.message || t('stock.exportError', 'Failed to export stock'));
    } finally {
      setExporting(false);
    }
  };

  // ── Expand row toggle ───────────────────────────────────────────────
  const toggleRowExpansion = (itemId) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  // ── Pagination ──────────────────────────────────────────────────────
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // ── Dialog open/close ───────────────────────────────────────────────
  const handleOpenDialog = (item = null) => {
    setSelectedItem(item);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItem(null);
  };

  // ── Delete ──────────────────────────────────────────────────────────
  const handleDelete = async () => {
    try {
      setLoading(true);
      await stockAPI.delete(selectedItem._id);
      toast.success(t('stock.deleteSuccess'));
      setOpenConfirm(false);
      fetchStockItems();
    } catch (err) {
      toast.error(err.response?.data?.message || t('stock.deleteError'));
    } finally {
      setLoading(false);
    }
  };

  // ── Open dialogs ────────────────────────────────────────────────────
  const handleOpenSnapshots = (item, branchId, branchName) => {
    setSnapshotsDialog({ open: true, item, branchId, branchName });
  };

  const handleOpenWACHistory = (item, branchId, branchName) => {
    setWacHistoryDialog({ open: true, item, branchId, branchName });
  };

  const handleOpenTransfer = (item) => {
    setTransferDialog({ open: true, item });
  };

  const handleOpenAudit = (item) => {
    setAuditDialog({ open: true, item });
  };

  // ── Permissions ─────────────────────────────────────────────────────
  const canView = isSuperAdmin || user?.privileges?.some(p => p.module === 'stock' && p.operations.includes('view'));
  const canAdd = isSuperAdmin || user?.privileges?.some(p => p.module === 'stock' && p.operations.includes('create'));
  const canEdit = isSuperAdmin || user?.privileges?.some(p => p.module === 'stock' && p.operations.includes('update'));
  const canDelete = isSuperAdmin || user?.privileges?.some(p => p.module === 'stock' && p.operations.includes('delete'));
  const canExport = canView;
  const canTransfer = isSuperAdmin || user?.privileges?.some(p => p.module === 'stock' && p.operations.includes('update'));

  const displayedBranches = selectedBranch
    ? branches.filter(b => b._id === selectedBranch)
    : branches;

  const hasActiveFilters =
    filters.lowStock || filters.expired || filters.showInactive ||
    searchQuery || selectedBranch || selectedCategory;

  if (privilegeError || !canView) {
    return <NoPrivilege message={t('stock.noPrivilegeError')} />;
  }

  const getBranchWac = (item, branchId) => {
    if (item.branchWac) {
      if (typeof item.branchWac === 'object') {
        return item.branchWac[branchId] || 0;
      }
    }
    if (item.currentWac !== undefined) {
      return item.currentWac;
    }
    return 0;
  };

  return (
    <Box sx={{ p: 3 }}>

      {/* ── Header ── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h4" component="h1">
            {t('stock.title', 'Stock Management')}
          </Typography>
          {!loading && (
            <Chip
              label={`${totalDocs} ${t('common.total', 'total')}`}
              color="primary" variant="outlined" size="small"
            />
          )}
        </Box>

        <Stack direction="row" spacing={1}>
          {canExport && (
            <Button
              variant="outlined"
              startIcon={exporting ? <CircularProgress size={18} /> : <FileDownloadIcon />}
              onClick={handleExport}
              disabled={exporting || loading}
            >
              {exporting
                ? t('common.exporting', 'Exporting...')
                : t('common.export', 'Export')}
            </Button>
          )}
          {canTransfer && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<SwapHorizIcon />}
              onClick={() => {
                toast.info(t('stock.selectItemToTransfer', 'Select a stock item from the table to transfer'));
              }}
            >
              {t('stock.transfer', 'Transfer')}
            </Button>
          )}
          {canAdd && (
            <Fab color="primary" size="medium" onClick={() => handleOpenDialog()}>
              <AddIcon />
            </Fab>
          )}
        </Stack>
      </Box>

      {/* ── Filters ── */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" gap={2} mb={2}>
          <FilterList />
          <Typography variant="h6">{t('filters', 'Filters')}</Typography>
        </Stack>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder={t('stock.searchPlaceholder', 'Search by name...')}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                endAdornment: searchInput && (
                  <IconButton size="small" onClick={() => setSearchInput('')}>
                    <ClearIcon />
                  </IconButton>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>{t('branches.title', 'Branch')}</InputLabel>
              <Select
                value={selectedBranch}
                onChange={e => { setSelectedBranch(e.target.value); setPage(0); }}
                label={t('branches.title', 'Branch')}
                disabled={!isSuperAdmin}
              >
                {isSuperAdmin && (
                  <MenuItem value="">
                    <em>{t('common.all', 'All Branches')}</em>
                  </MenuItem>
                )}
                {branches.map(b => (
                  <MenuItem key={b._id} value={b._id}>{b.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>
                {categoriesLoading
                  ? t('common.loading', 'Loading...')
                  : t('stock.category', 'Category')}
              </InputLabel>
              <Select
                value={selectedCategory}
                onChange={e => { setSelectedCategory(e.target.value); setPage(0); }}
                label={t('stock.category', 'Category')}
                disabled={categoriesLoading}
              >
                <MenuItem value="">
                  <em>{t('common.all', 'All Categories')}</em>
                </MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat._id} value={cat._id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
              <Button
                variant={filters.lowStock ? 'contained' : 'outlined'}
                color="warning"
                startIcon={<WarningIcon />}
                onClick={() => handleFilterChange('lowStock', !filters.lowStock)}
                size="small"
              >
                {t('stock.lowStock', 'Low Stock')}
              </Button>
              <Button
                variant={filters.expired ? 'contained' : 'outlined'}
                color="error"
                startIcon={<WarningIcon />}
                onClick={() => handleFilterChange('expired', !filters.expired)}
                size="small"
              >
                {t('stock.expired', 'Expired')}
              </Button>
              <Button
                variant={filters.showInactive ? 'contained' : 'outlined'}
                color="info"
                onClick={() => handleFilterChange('showInactive', !filters.showInactive)}
                size="small"
              >
                {filters.showInactive
                  ? t('stock.showingAll', 'Showing All')
                  : t('stock.activeOnly', 'Active Only')}
              </Button>
              {hasActiveFilters && (
                <Button
                  variant="text" color="inherit"
                  onClick={handleClearFilters}
                  startIcon={<ClearIcon />}
                  size="small"
                >
                  {t('common.clearFilters', 'Clear')}
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* ── Active filter chips ── */}
      {hasActiveFilters && (
        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          {filters.lowStock && (
            <Chip label={t('stock.lowStock', 'Low Stock')} color="warning" size="small"
              onDelete={() => handleFilterChange('lowStock', false)} />
          )}
          {filters.expired && (
            <Chip label={t('stock.expired', 'Expired')} color="error" size="small"
              onDelete={() => handleFilterChange('expired', false)} />
          )}
          {filters.showInactive && (
            <Chip label={t('stock.includingInactive', 'Including Inactive')} color="info" size="small"
              onDelete={() => handleFilterChange('showInactive', false)} />
          )}
          {searchQuery && (
            <Chip
              label={`${t('common.search', 'Search')}: ${searchQuery}`}
              size="small"
              onDelete={() => setSearchInput('')}
            />
          )}
          {selectedCategory && (
            <Chip
              label={`${t('stock.category', 'Category')}: ${categories.find(c => c._id === selectedCategory)?.name ?? selectedCategory}`}
              size="small"
              onDelete={() => { setSelectedCategory(''); setPage(0); }}
            />
          )}
        </Box>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('common.showing', 'Showing')}{' '}
        {totalDocs === 0 ? 0 : Math.min(page * rowsPerPage + 1, totalDocs)}–
        {Math.min((page + 1) * rowsPerPage, totalDocs)}{' '}
        {t('common.of', 'of')} {totalDocs} {t('stock.items', 'items')}
      </Typography>

      {/* ── Table ── */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>{t('stock.name', 'Stock Name')}</TableCell>
                <TableCell>{t('stock.category', 'Category')}</TableCell>
                <TableCell align="right">{t('stock.price', 'Price')}</TableCell>
                <TableCell align="right">{t('stock.minimumThreshold', 'Min. Threshold')}</TableCell>
                <TableCell>{t('stock.validUntil', 'Valid Until')}</TableCell>
                {displayedBranches.map(b => (
                  <TableCell key={b._id} align="center">
                    <Box>
                      <Typography variant="caption" fontWeight="bold">
                        {b.name}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {t('stock.qtyWac', 'Qty / WAC')}
                      </Typography>
                    </Box>
                  </TableCell>
                ))}
                <TableCell>{t('common.status', 'Status')}</TableCell>
                <TableCell>{t('common.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9 + displayedBranches.length} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : stockItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9 + displayedBranches.length} align="center">
                    <NoContent
                      message={
                        searchQuery
                          ? t('stock.noSearchResults', 'No items match your search')
                          : filters.lowStock
                            ? t('stock.noLowStockItems', 'No low stock items found')
                            : filters.expired
                              ? t('stock.noExpiredItems', 'No expired items found')
                              : t('stock.noItemsFound', 'No stock items found')
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : stockItems.map((item) => {
                const isExpired = item.validUntil && new Date(item.validUntil) < new Date();
                const isLowStock = displayedBranches.some(
                  b => (item.quantities?.[b._id] ?? 0) <= item.minimumThreshold
                );
                const categoryName = item.category?.name ?? '—';
                const isExpanded = expandedRows.has(item._id);

                return (
                  <React.Fragment key={item._id}>
                    <TableRow
                      hover
                      sx={{
                        opacity: item.isActive ? 1 : 0.6,
                        '& > *': { borderBottom: isExpanded ? 'unset' : undefined }
                      }}
                    >
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpansion(item._id)}
                        >
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>

                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" fontWeight="medium">
                            {item.name}
                          </Typography>
                          {!item.isActive && (
                            <Chip label={t('common.inactive', 'Inactive')}
                              size="small" variant="outlined" />
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>
                        {item.category
                          ? <Chip label={categoryName} size="small" variant="outlined" />
                          : <Typography variant="body2" color="text.secondary">—</Typography>
                        }
                      </TableCell>

                      <TableCell align="right">{item.price}</TableCell>
                      <TableCell align="right">{item.minimumThreshold}</TableCell>

                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {item.validUntil
                            ? format(new Date(item.validUntil), 'dd/MM/yyyy')
                            : '—'}
                          {isExpired && (
                            <Chip label={t('stock.expired', 'Expired')}
                              size="small" color="error" />
                          )}
                        </Box>
                      </TableCell>

                      {displayedBranches.map(b => {
                        const qty = item.quantities?.[b._id] ?? 0;
                        const wac = getBranchWac(item, b._id);
                        const low = qty <= item.minimumThreshold;
                        return (
                          <TableCell key={b._id} align="center">
                            <Box>
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                color={low ? 'warning.main' : 'text.primary'}
                              >
                                {qty}
                                {low && (
                                  <WarningIcon fontSize="small" color="warning"
                                    sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                                )}
                              </Typography>
                              {wac > 0 && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  WAC: {wac.toFixed(2)}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                        );
                      })}

                      <TableCell>
                        {isLowStock && !filters.lowStock && (
                          <Chip label={t('stock.lowStock', 'Low')}
                            size="small" color="warning" />
                        )}
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          {/* Audit Trail */}
                          {canView && (
                            <Tooltip title={t('stock.audit.viewHistory', 'View History')}>
                              <IconButton
                                size="small"
                                color="default"
                                onClick={() => handleOpenAudit(item)}
                              >
                                <HistoryIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canTransfer && (
                            <Tooltip title={t('stock.transferStock', 'Transfer Stock')}>
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => handleOpenTransfer(item)}
                              >
                                <SwapHorizIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canEdit && (
                            <Tooltip title={t('common.edit', 'Edit')}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(item)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canDelete && (
                            <Tooltip title={t('common.delete', 'Delete')}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => { setSelectedItem(item); setOpenConfirm(true); }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {/* Expanded Row */}
                    <TableRow>
                      <TableCell
                        colSpan={9 + displayedBranches.length}
                        sx={{ p: 0 }}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              <InventoryIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                              {t('stock.branchDetails', 'Branch Details for')} {item.name}
                            </Typography>

                            <Grid container spacing={2}>
                              {displayedBranches.map(branch => {
                                const qty = item.quantities?.[branch._id] ?? 0;
                                const wac = getBranchWac(item, branch._id);
                                const totalValue = qty * wac;
                                const low = qty <= item.minimumThreshold;

                                return (
                                  <Grid item xs={12} sm={6} md={4} key={branch._id}>
                                    <Paper
                                      variant="outlined"
                                      sx={{
                                        p: 2,
                                        borderColor: low ? 'warning.main' : 'divider',
                                        borderWidth: low ? 2 : 1
                                      }}
                                    >
                                      <Typography variant="subtitle2" gutterBottom>
                                        {branch.name}
                                        {low && (
                                          <WarningIcon
                                            fontSize="small"
                                            color="warning"
                                            sx={{ ml: 1, verticalAlign: 'middle' }}
                                          />
                                        )}
                                      </Typography>

                                      <Stack spacing={0.5}>
                                        <Box display="flex" justifyContent="space-between">
                                          <Typography variant="body2" color="text.secondary">
                                            {t('stock.quantity', 'Quantity')}:
                                          </Typography>
                                          <Typography variant="body2" fontWeight="bold">
                                            {qty} {item.unit}
                                          </Typography>
                                        </Box>
                                        <Box display="flex" justifyContent="space-between">
                                          <Typography variant="body2" color="text.secondary">
                                            {t('stock.weightedAvgCost', 'WAC')}:
                                          </Typography>
                                          <Typography variant="body2" fontWeight="bold">
                                            {wac.toFixed(2)}
                                          </Typography>
                                        </Box>
                                        <Box display="flex" justifyContent="space-between">
                                          <Typography variant="body2" color="text.secondary">
                                            {t('stock.totalValue', 'Total Value')}:
                                          </Typography>
                                          <Typography variant="body2" fontWeight="bold" color="primary.main">
                                            {totalValue.toFixed(2)}
                                          </Typography>
                                        </Box>
                                      </Stack>

                                      <Stack direction="row" spacing={0.5} mt={1.5}>
                                        <Tooltip title={t('stock.viewDailySnapshots', 'View Daily Snapshots')}>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CalendarIcon />}
                                            onClick={() => handleOpenSnapshots(item, branch._id, branch.name)}
                                            fullWidth
                                          >
                                            {t('stock.daily', 'Daily')}
                                          </Button>
                                        </Tooltip>
                                        <Tooltip title={t('stock.viewWACHistory', 'View WAC History')}>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="info"
                                            startIcon={<TrendingUpIcon />}
                                            onClick={() => handleOpenWACHistory(item, branch._id, branch.name)}
                                            fullWidth
                                          >
                                            {t('stock.wac', 'WAC')}
                                          </Button>
                                        </Tooltip>
                                      </Stack>
                                    </Paper>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ mt: 2 }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalDocs}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('common.rowsPerPage', 'Rows per page')}
        />
      </Paper>

      {/* ── Add / Edit Dialog ── */}
      <StockForm
        open={openDialog}
        onClose={handleCloseDialog}
        selectedItem={selectedItem}
        branches={branches}
        categories={categories}
        categoriesLoading={categoriesLoading}
        onSuccess={fetchStockItems}
        isSuperAdmin={isSuperAdmin}
        user={user}
      />

      {/* ── Confirm Delete ── */}
      <ConfirmDialog
        open={openConfirm}
        title={t('stock.deleteConfirmTitle')}
        message={t('stock.deleteConfirmMessage')}
        onConfirm={handleDelete}
        onClose={() => setOpenConfirm(false)}
        loading={loading}
      />

      {/* ── Daily Snapshots Dialog ── */}
      <StockDailySnapshots
        open={snapshotsDialog.open}
        onClose={() => setSnapshotsDialog({ open: false, item: null, branchId: null, branchName: null })}
        stockItem={snapshotsDialog.item}
        branchId={snapshotsDialog.branchId}
        branchName={snapshotsDialog.branchName}
      />

      {/* ── WAC History Dialog ── */}
      <StockWACHistory
        open={wacHistoryDialog.open}
        onClose={() => setWacHistoryDialog({ open: false, item: null, branchId: null, branchName: null })}
        stockItem={wacHistoryDialog.item}
        branchId={wacHistoryDialog.branchId}
        branchName={wacHistoryDialog.branchName}
      />

      {/* ── Branch Transfer Dialog ── */}
      <BranchTransferDialog
        open={transferDialog.open}
        onClose={() => setTransferDialog({ open: false, item: null })}
        stockItem={transferDialog.item}
        branches={branches}
        onSuccess={fetchStockItems}
      />

      {/* ── Stock Audit Dialog ── */}
      <StockAuditDialog
        open={auditDialog.open}
        onClose={() => setAuditDialog({ open: false, item: null })}
        stockItem={auditDialog.item}
      />
    </Box>
  );
};

export default Stock;